import { useCallback, useEffect, useRef, useState } from 'preact/hooks';
import { haptic } from '../lib/haptics.js';
import * as api from '../lib/api.js';
import type { RecordingDay, Take } from '../lib/api.js';

/**
 * The archive of past shows, for the two hosts.
 *
 * Deliberately behind a tap rather than on the home screen. Host home is one
 * button on purpose — the thing you do at 8pm is go live, not browse. This
 * opens over it and closes back to it.
 */

const prettyDay = (day: string): string => {
  const [year, month, date] = day.split('-').map(Number);
  if (!year || !month || !date) return day;
  return new Intl.DateTimeFormat(undefined, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(new Date(Date.UTC(year, month - 1, date)));
};

const prettySize = (bytes: number): string =>
  bytes >= 1e9 ? `${(bytes / 1e9).toFixed(1)} GB` : `${Math.max(1, Math.round(bytes / 1e6))} MB`;

const prettyTime = (iso: string): string => {
  const at = new Date(iso);
  return Number.isNaN(at.getTime())
    ? ''
    : at.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
};

function TakeRow({ take, label }: { take: Take; label: string }) {
  const [open, setOpen] = useState(false);
  const video = useRef<HTMLVideoElement>(null);

  /*
   * `<video>` even though most shows are voice only.
   *
   * The archive is one MP4 either way, and a video element plays an audio-only
   * MP4 perfectly well — it just shows a black frame. An <audio> element, by
   * contrast, refuses the ones that DO have video. One element handles both.
   */
  useEffect(() => {
    if (open) video.current?.play().catch(() => { /* the controls are right there */ });
  }, [open]);

  return (
    <li class="rec__take">
      <div class="rec__takehead">
        <div>
          <p class="rec__takelabel">{label}</p>
          <p class="t-foot">
            {prettyTime(take.startedAt)} · {prettySize(take.sizeBytes)}
          </p>
        </div>
        <div class="rec__actions">
          <button
            class="rec__btn"
            aria-expanded={open}
            onClick={() => {
              haptic('press');
              setOpen((was) => !was);
            }}
          >
            {open ? 'Close' : 'Play'}
          </button>
          {/*
            A real link, not a fetch-and-blob. The file can be several GB, and
            pulling that through JavaScript would hold the whole archive in the
            tab's memory before writing a byte. The browser streams it to disk.
          */}
          <a class="rec__btn rec__btn--quiet" href={take.downloadUrl} download>
            Download
          </a>
        </div>
      </div>
      {open ? (
        <video
          ref={video}
          class="rec__player"
          src={take.url}
          controls
          playsinline
          preload="metadata"
        />
      ) : null}
    </li>
  );
}

function DayList({
  entries,
  takeLabel,
  dayPrefix = '',
}: {
  entries: RecordingDay[];
  takeLabel: (count: number, index: number) => string;
  dayPrefix?: string;
}) {
  return (
    <>
      {entries.map((entry) => (
        <section key={`${dayPrefix}${entry.day}`} class="rec__day">
          <h2 class="rec__dayname">
            {dayPrefix}
            {prettyDay(entry.day)}
          </h2>
          <ul class="rec__takes">
            {entry.takes.map((take, index) => (
              <TakeRow key={take.key} take={take} label={takeLabel(entry.takes.length, index)} />
            ))}
          </ul>
        </section>
      ))}
    </>
  );
}

export function Recordings({ onClose }: { onClose: () => void }) {
  const [days, setDays] = useState<RecordingDay[] | null>(null);
  const [calls, setCalls] = useState<RecordingDay[]>([]);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const result = await api.listRecordings();
      setDays(result.days);
      setCalls(result.calls ?? []);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Could not load recordings.');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <main class="rec">
      <header class="rec__head">
        <button class="rec__back" onClick={onClose} aria-label="Back">
          ‹ Back
        </button>
        <h1 class="rec__title">Recordings</h1>
      </header>

      {error ? (
        <div class="notice notice--bad">
          <p>{error}</p>
          <button class="rec__btn" onClick={() => void load()}>
            Try again
          </button>
        </div>
      ) : null}

      {days === null && !error ? <p class="t-foot">Loading…</p> : null}

      {days?.length === 0 && calls.length === 0 ? (
        <p class="t-foot">
          Nothing recorded yet. Shows are archived automatically once recording is
          switched on, and calls between the hosts are recorded as they happen.
        </p>
      ) : null}

      {days ? (
        <DayList
          entries={days}
          /*
           * A day usually has one recording. It has more when a show was stopped
           * and restarted, and then "Part 2" is what actually happened — not a
           * second show.
           */
          takeLabel={(count, index) => (count > 1 ? `Part ${count - index}` : 'Full show')}
        />
      ) : null}

      <DayList
        entries={calls}
        dayPrefix="Calls · "
        takeLabel={(count, index) => (count > 1 ? `Call ${count - index}` : 'Call')}
      />

      <p class="rec__hint">
        Links are private to you and expire after a few hours. Re-open this screen
        for fresh ones.
      </p>
    </main>
  );
}
